import React from 'react';
import type DropViewState from '../store/schema/DropViewState';
import type DropEffect from '../store/schema/DropEffect';
import setDropState from '../actions/setDropState';
import type { DragData } from '../utils/dragDataUtil';
import { getDragItemType, getDragItemData, setDragItemDetails } from '../utils/dragDataUtil';
import { DraggableItemTypes } from '../utils/DraggableItemTypes';
import { DEFAULT_ITEM_TYPE, FIREFOX_DOCTYPE } from '../utils/constants';
import { getDragData } from '../utils/getDragData';

export interface DroppableProps {
    // A callback function to call when drop
    onDrop: (
        dragData: DragData,
        pageX: number,
        pageY: number,
        target?: HTMLElement,
        ctrlKey?: boolean
    ) => void;

    // ViewState object, use this object to render different layout when dragging over
    dropViewState: DropViewState;

    // (Optional) A callback function to check if current drag data is able to drop on current element
    // The boolean return value here is deprecated, you should use DropEffect instead.
    canDrop?: (
        dragInfo: DragData,
        dragEvent?: React.DragEvent<HTMLElement>
    ) => boolean | DropEffect;

    // (Optional) A callback function to call when drag over this element
    onDragOver?: (
        dragInfo: DragData,
        pageX: number,
        pageY: number,
        target?: HTMLElement,
        ctrlKey?: boolean
    ) => void;

    // (Optional) Flag for if this component will act upon the item
    bypassActOnDrop?: boolean;

    // (Deprecated) (Optional) whether to use copy as drop effect
    shouldUseCopyAsDropEffect?: boolean;

    // (Optional) A callback function to call when drag leave this element
    onDragLeave?: (dragInfo: DragData, target?: HTMLElement) => void;

    // (Optional) A callback function to call when a item is being dragged enter this element.
    onDragEnter?: (dragInfo: DragData) => void;

    // (Optional) CSS Class name of this element
    classNames?: string;

    // (Optional) Inline css styles for this element
    style?: React.CSSProperties;

    // Used to indicate whether a drag over a child element should be considered a
    // drag over the parent as well
    greedy?: boolean;

    // (Optional) Whether to ignore transient onDragLeave event
    shouldIgnoreTransientOnDragLeave?: boolean;

    // (Optional) Whether to prevent drag events from propagating through
    preventBubblingDragEvents?: boolean;

    children?: React.ReactNode;

    id?: string;

    // according to https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/drop_event
    // we need to call preventDefault on dragover event to ensure drop event to fire correctly
    preventDragOverDefault?: boolean;

    // (Optional) When true, this component renders a plain DIV with no drag/drop
    // event handlers attached. Defaults to false.
    disabled?: boolean;
}

const DRAG_LEAVE_HANDLER_DELAY_IN_MILLISECONDS = 50;

// This is a helper class for better code reusing, this should be only invoked from owadnd classes
export class DroppableCore {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2564 (71,13): Property 'div' has no initializer and is not definitely assigned in the constructor.
    // @ts-expect-error
    private div: HTMLDivElement;

    // We receive a lot of onDragLeave and onDragOver events during hovering over.
    // onDragLeave sets false to isDragOver, onDragOver sets true to isDragOver.
    // Then, isDragOver property changes frequently and would cause UI flicking.
    // So we introduce a delay for the onDragLeave handler to ignore the transient onDragLeave event to avoid the UI flicking.
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7008 (80,13): Member 'pendingDragLeaveHandler' implicitly has an 'any' type.
    // @ts-expect-error
    private pendingDragLeaveHandler;

    constructor(private props: DroppableProps) {}

    updateProps(props: any): void {
        this.props = props;
    }

    // OnDragEnter event handler
    onDragEnter = (dragEvent: React.DragEvent<HTMLElement>): void => {
        dragEvent.preventDefault();
        if (this.props.preventBubblingDragEvents) {
            dragEvent.stopPropagation();
        }
        //For edge when a draggable item is leaving the droppable
        //the dropEffect is stuck in the value when item was in
        //the original droppable. We are setting the drop effect
        // manually, so in drag enter we need to set the dropEffect
        // to the correct value.
        const dragData = getDragData(dragEvent.dataTransfer);
        if (this.props.onDragEnter) {
            this.props.onDragEnter(dragData);
        }
    };

    // OnDragLeave event handler
    onDragLeave = (dragEvent: React.DragEvent<HTMLElement>): void => {
        // The HTML5 API will fire onDragLeave when the user drags over any children of the droppable
        // We want to ignore this here and treat this as being still inside the droppable area.
        if (this.props.preventBubblingDragEvents) {
            dragEvent.stopPropagation();
        }

        if (this.props.greedy && !this.isReallyLeavingDroppable(dragEvent)) {
            return;
        }

        let dragType: string | null = this.props.dropViewState.draggableItemType;
        if (!dragType) {
            dragType = this.getDragType(dragEvent);
        }
        const dragData: DragData = { itemType: dragType, itemData: getDragItemData() };

        if (this.getDesiredDropEffect(dragData, dragEvent) == 'none') {
            return;
        }

        const handler = (data: DragData) => () => {
            if (this.props.onDragLeave) {
                this.props.onDragLeave(data, dragEvent.currentTarget);
            }
            setDropState(
                this.props.dropViewState,
                false /*isDragOver*/,
                null /*draggableItemType*/
            );
            this.pendingDragLeaveHandler = null;
        };

        if (!this.props.shouldIgnoreTransientOnDragLeave) {
            handler(dragData)();
        } else if (!this.pendingDragLeaveHandler) {
            dragEvent.persist();
            this.pendingDragLeaveHandler = setTimeout(
                handler(dragData),
                DRAG_LEAVE_HANDLER_DELAY_IN_MILLISECONDS
            );
        }
    };

    // OnDragOver event handler
    onDragOver = (dragEvent: React.DragEvent<HTMLElement>): void => {
        if (this.props.preventBubblingDragEvents) {
            dragEvent.stopPropagation();
        }

        if (this.props.preventDragOverDefault) {
            dragEvent.preventDefault();
        }

        let dragType: string | null = this.props.dropViewState.draggableItemType;
        if (!dragType) {
            dragType = this.getDragType(dragEvent);
        }
        const dragData: DragData = { itemType: dragType, itemData: getDragItemData() };

        const dropEffect = this.getDesiredDropEffect(dragData, dragEvent);

        // If we should not handle events, we should stop processing.
        if (dropEffect == 'none') {
            // If we're bypassing actions on drop, we don't want to modify the dropEffect.
            // This is often when there's a valid drop target above us in the document tree.
            // If this is a valid drop target, we should reset the drop effect to "none".
            // This sets a proper drag hint icon.
            if (!this.props.bypassActOnDrop) {
                this.setDropEffectInternal(dragEvent, 'none');
            }
            return;
        }

        if (!this.props.bypassActOnDrop) {
            dragEvent.preventDefault(); // adding preventDefault allows the drop to happen
            this.setDropEffectInternal(dragEvent, dropEffect);
        }

        if (this.props.shouldUseCopyAsDropEffect) {
            this.setDropEffectInternal(dragEvent, 'copy');
        }

        if (this.props.onDragOver) {
            this.props.onDragOver(
                dragData,
                dragEvent.pageX,
                dragEvent.pageY,
                dragEvent.currentTarget,
                dragEvent.ctrlKey || dragEvent.metaKey
            );
        }

        this.cancelPendingDragLeaveHandler();
        if (
            !this.props.dropViewState.isDragOver ||
            this.props.dropViewState.draggableItemType != dragType
        ) {
            setDropState(this.props.dropViewState, true /*isDragOver*/, dragType);
        }
    };

    // OnDrop event handler
    onDrop = (dragEvent: React.DragEvent<HTMLElement>): void => {
        if (this.props.preventBubblingDragEvents) {
            dragEvent.stopPropagation();
        }

        const dragData = getDragData(dragEvent.dataTransfer);

        // Need this, otherwise FireFox gets redirected.
        if (dragData?.itemType != DEFAULT_ITEM_TYPE) {
            dragEvent.preventDefault();
        }

        if (this.getDesiredDropEffect(dragData, dragEvent) == 'none') {
            return;
        }

        this.props.onDrop(
            dragData,
            dragEvent.pageX,
            dragEvent.pageY,
            dragEvent.currentTarget,
            dragEvent.ctrlKey || dragEvent.metaKey
        );

        // TODO VSO 13903: Some times components need extra info to determine if the dropping action is on the component itself
        // or another component which shares the same store object
        // We call this callback here to allow the component do some extra work.
        if (this.props.onDragLeave) {
            this.props.onDragLeave(dragData, dragEvent.currentTarget);
        }

        this.cancelPendingDragLeaveHandler();
        setDragItemDetails(null, null);
        setDropState(this.props.dropViewState, false /*isDragOver*/, null /*draggableItemType*/);
    };

    refCallback = (ref: HTMLDivElement): void => {
        this.div = ref;
    };

    getDiv(): HTMLDivElement {
        return this.div;
    }

    cancelPendingDragLeaveHandler(): void {
        if (this.pendingDragLeaveHandler) {
            clearTimeout(this.pendingDragLeaveHandler);
            this.pendingDragLeaveHandler = null;
        }
    }

    /**
     * Check whether we are *really* leaving the droppable area during a dragleave event
     * @param dragEvent The dragleave event
     * @returns True if we are in fact leaving the droppable, false otherwise
     */
    private isReallyLeavingDroppable(dragEvent: React.DragEvent<HTMLElement>): boolean {
        const hoveredElement = document.elementFromPoint(
            dragEvent.pageX,
            dragEvent.pageY
        ) as HTMLElement;
        return !this.isDescendantOrSelf(hoveredElement, dragEvent.currentTarget);
    }

    private isDescendantOrSelf(element: HTMLElement, ancestor: HTMLElement): boolean {
        if (!element || !ancestor) {
            return false;
        }
        if (element == ancestor) {
            return true;
        }
        var parent = element;
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (278,17): Type 'HTMLElement | null' is not assignable to type 'HTMLElement'.
        // @ts-expect-error
        while ((parent = parent.parentElement)) {
            if (parent == ancestor) {
                return true;
            }
        }
        return false;
    }

    private getDesiredDropEffect(
        dragData: DragData,
        dragEvent: React.DragEvent<HTMLElement>
    ): DropEffect {
        // DragData can be null, if the drag has happened not from the dragContext source.
        if (dragData) {
            if (this.props.canDrop) {
                if (typeof this.props.canDrop(dragData, dragEvent) === 'boolean') {
                    // For legacy scenarios returning boolean values, provide a sensible default.
                    return this.props.canDrop(dragData, dragEvent) ? 'move' : 'none';
                } else {
                    return this.props.canDrop(dragData, dragEvent) as DropEffect;
                }
            } else {
                // If there's no canDrop method, provide a reasonable default.
                return 'move';
            }
        } else {
            // With no DragData, return 'none' by default.
            return 'none';
        }
    }

    private getDragType(dragEvent: React.DragEvent<HTMLElement>): string | null {
        let dragType = getDragItemType();
        const dataTransfer = dragEvent.dataTransfer
            ? dragEvent.dataTransfer
            : dragEvent.nativeEvent.dataTransfer;
        if (
            !dragType &&
            dataTransfer &&
            dataTransfer.types &&
            dataTransfer.types.length > 0 &&
            (dataTransfer.types.includes(FIREFOX_DOCTYPE) || dataTransfer.types.length === 1)
        ) {
            // We want the first type available that is on our list of supported types (as determined by DraggableItemTypes)
            const typesArray = Array.prototype.slice.apply(dragEvent.dataTransfer.types);
            const draggableTypes = Object.values(DraggableItemTypes);
            const filteredTypes = typesArray.filter(type => {
                return draggableTypes.indexOf(type) >= 0;
            });
            if (filteredTypes.length > 0) {
                dragType = filteredTypes[0];
            }
        }
        return dragType;
    }

    private setDropEffectInternal(
        dragEvent: {
            dataTransfer: {
                dropEffect: string;
            };
        },
        dropEffect: DropEffect
    ) {
        if (dragEvent.dataTransfer) {
            dragEvent.dataTransfer.dropEffect = dropEffect;
        }
    }
}

export interface DroppableHandle {
    getDiv(): HTMLDivElement;
}

// TODO: Bug 14032: Consider a framework-independent design of owa-dnd
const defaultExport: React.ForwardRefExoticComponent<
    DroppableProps & React.RefAttributes<DroppableHandle>
> = React.forwardRef(function Droppable(props: DroppableProps, ref: React.Ref<DroppableHandle>) {
    const core = React.useRef<DroppableCore>(new DroppableCore(props));
    // In case the props on the droppable component change, we need to set them
    // explicitly on the DroppableCore's instance. The useRef holds onto the same instance
    // of object
    core.current.updateProps(props);
    React.useImperativeHandle(
        ref,
        () => ({
            getDiv() {
                return core.current.getDiv();
            },
        }),
        []
    );
    const disabled = props.disabled ?? false;
    return (
        <div
            ref={core.current.refCallback}
            className={props.classNames}
            style={props.style}
            onDrop={disabled ? undefined : core.current.onDrop}
            onDragOver={disabled ? undefined : core.current.onDragOver}
            onDragEnter={disabled ? undefined : core.current.onDragEnter}
            onDragLeave={disabled ? undefined : core.current.onDragLeave}
            id={props.id}
        >
            {props.children}
        </div>
    );
});
export default defaultExport;
